// The team radar as an engraving: thin sepia rings, hairline spokes, the team's own colour at low
// opacity for the filled shape. Inline SVG, same approach as heroIcons.ts's iconSvg — no chart library
// for six points.
//
// The viewBox is deliberately much larger than the hexagon itself: a label at the rim of a six-point
// radar sits well outside the plot area, and centring every label on its axis point (text-anchor:middle)
// clips the two side labels no matter how much padding is added, because the text grows on BOTH sides of
// a point that is already near an edge. Anchoring each label away from the centre — start/end for the
// four diagonal axes, middle only for the vertical top/bottom pair — makes it grow into the empty margin
// instead, which is what padding can actually fix.
import { AXES, axisLabel, strengthSummary, type TeamProfile } from './teamProfile.js';
import { t } from './i18n/index.js';

const CX = 120, CY = 95, R = 55, LABEL_R = 1.22;
const VIEWBOX = `0 0 240 200`;
const RING_LEVELS = [1 / 3, 2 / 3, 1];

function axisAngle(i: number): number {
  return -Math.PI / 2 + i * (2 * Math.PI / AXES.length);
}
function axisPoint(i: number, v: number): [number, number] {
  const a = axisAngle(i);
  return [CX + Math.cos(a) * R * v, CY + Math.sin(a) * R * v];
}
function labelAnchor(i: number): 'start' | 'middle' | 'end' {
  const cosA = Math.cos(axisAngle(i));
  if (Math.abs(cosA) < 0.2) return 'middle';  // the top/bottom pair
  return cosA > 0 ? 'start' : 'end';
}
function ring(level: number): string {
  return AXES.map((_, i) => axisPoint(i, level).join(',')).join(' ');
}

export function radarSummaryText(profile: TeamProfile | null): string {
  if (!profile) return t('radar.empty');
  const { strong, weak } = strengthSummary(profile);
  return t('radar.summary', { strong: strong.map(axisLabel).join(', '), weak: weak.map(axisLabel).join(', ') });
}

export function radarChartSvg(profile: TeamProfile | null, color: string): string {
  const rings = RING_LEVELS.map(lv => `<polygon class="radar-ring" points="${ring(lv)}"/>`).join('');
  const spokes = AXES.map((_, i) => { const [x, y] = axisPoint(i, 1); return `<line class="radar-spoke" x1="${CX}" y1="${CY}" x2="${x}" y2="${y}"/>`; }).join('');
  const labels = AXES.map((axis, i) => {
    const [x, y] = axisPoint(i, LABEL_R);
    return `<text class="radar-label" x="${x}" y="${y}" text-anchor="${labelAnchor(i)}" dominant-baseline="middle">${axisLabel(axis)}</text>`;
  }).join('');
  const shape = profile
    ? `<polygon class="radar-shape" points="${AXES.map((a, i) => axisPoint(i, profile[a]).join(',')).join(' ')}" style="fill:${color};stroke:${color}"/>`
    : '';
  return `<svg class="radar" viewBox="${VIEWBOX}" role="img" aria-label="${radarSummaryText(profile)}">${rings}${spokes}${shape}${labels}</svg>`;
}
